import Cookies from 'js-cookie';
import ky from 'ky';

type ChannelIOFn = ((...args: unknown[]) => void) & {
	c?: (args: unknown) => void;
	q?: unknown[];
};

declare global {
	interface Window {
		ChannelIO?: ChannelIOFn;
		ChannelIOInitialized?: boolean;
	}
}

let scriptLoading = false;
let initializing = false;
let booted = false;

const loadScript = () => {
	if (window.ChannelIO || scriptLoading) return;

	scriptLoading = true;

	const ch = ((...args: unknown[]) => {
		ch.c?.(args);
	}) as ChannelIOFn;
	ch.q = [];
	ch.c = (args) => {
		ch.q?.push(args);
	};
	window.ChannelIO = ch;

	const insertPlugin = () => {
		if (window.ChannelIOInitialized) return;

		window.ChannelIOInitialized = true;
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.async = true;
		script.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
		script.charset = 'UTF-8';
		const firstScript = document.getElementsByTagName('script')[0];
		firstScript.parentNode?.insertBefore(script, firstScript);
	};

	if (document.readyState === 'complete') {
		insertPlugin();
	} else {
		window.addEventListener('DOMContentLoaded', insertPlugin, { once: true });
		window.addEventListener('load', insertPlugin, { once: true });
	}
};

const waitForChannelIO = () => {
	if (window.ChannelIO) return Promise.resolve();

	return new Promise<void>((resolve) => {
		const startedAt = Date.now();
		const poll = window.setInterval(() => {
			if (window.ChannelIO || Date.now() - startedAt > 10_000) {
				window.clearInterval(poll);
				resolve();
			}
		}, 50);
	});
};

export const initChannelIO = async () => {
	if (booted || initializing || typeof window === 'undefined') return;
	initializing = true;

	try {
		loadScript();
		await waitForChannelIO();

		if (booted || !window.ChannelIO) return;
		if (process.env.NODE_ENV === 'local') return;

		const config = await getChannelIOConfig({ memberId: getMemberId() });
		if (booted || !window.ChannelIO) return;

		window.ChannelIO('boot', { ...(config ?? {}) });
		booted = true;
	} catch (error) {
		console.error(error);
		initializing = false;
	}
};

export const showChannelTalk = () => {
	window.ChannelIO?.('showMessenger');
};

const getChannelIOConfig = ({ memberId }: { memberId: string }) => {
	return ky
		.get('users/getChannelIOConfig', {
			prefix: process.env.GATSBY_DONUE_ACCOUNT_API_HOST,
			headers: {
				session: Cookies.get('session') || ''
			},
			searchParams: { memberId }
		})
		.json<{ payload?: Record<string, unknown> }>()
		.then((data) => data?.payload);
};

const getMemberId = () => {
	const memberId = Cookies.get('donue-ch-memberId') ?? crypto.randomUUID();
	Cookies.set('donue-ch-memberId', memberId, { expires: 1 });
	return memberId;
};
